import type { CaptionSpeakerColorVariant } from './captionSpeakerColors';

import type { TranscriptSpeakerRole } from 'features/voice-meeting/interfaces';

export interface CaptionLineForTurns {
  resultId: string;
  text: string;
  isPartial: boolean;
  speakerLabel: string;
  speakerRole: TranscriptSpeakerRole;
  speakerColorVariant: CaptionSpeakerColorVariant;
  receivedAtMs: number;
  startedAtMs?: number;
  endedAtMs?: number;
}

export interface CaptionTurn {
  key: string;
  resultIds: string[];
  speakerLabel: string;
  speakerRole: TranscriptSpeakerRole;
  speakerColorVariant: CaptionSpeakerColorVariant;
  text: string;
}

export interface BuildCaptionTurnsOptions {
  // When true, same-speaker segments always merge regardless of silence gap.
  // Set via LD when captionDisplayPauseThresholdMs === 0 (see getCaptionTurnBuildOptions).
  mergeOnPause: boolean;
  // Minimum silence between finalized segments before starting a new displayed turn.
  pauseThresholdMs: number;
}

const createTurn = (line: CaptionLineForTurns): CaptionTurn => ({
  // Stable React key and prune anchor — always the first segment in a merged turn.
  key: line.resultId,
  // All underlying ASR resultIds merged into this turn (used for line pruning).
  resultIds: [line.resultId],
  speakerLabel: line.speakerLabel,
  speakerRole: line.speakerRole,
  speakerColorVariant: line.speakerColorVariant,
  text: line.text,
});

const getSegmentGapMs = (
  previousLine: CaptionLineForTurns,
  incomingLine: CaptionLineForTurns
): number => {
  // Prefer Chime audio timestamps when present — they reflect actual speech timing
  // rather than when the transcript arrived over the network.
  const previousEnd =
    previousLine.endedAtMs ??
    previousLine.startedAtMs ??
    previousLine.receivedAtMs;
  const incomingStart = incomingLine.startedAtMs ?? incomingLine.receivedAtMs;

  return incomingStart - previousEnd;
};

const shouldSplitOnPause = (
  previousLine: CaptionLineForTurns,
  incomingLine: CaptionLineForTurns,
  options: BuildCaptionTurnsOptions
): boolean => {
  if (options.mergeOnPause) {
    return false;
  }

  // A partial previous segment is still the same in-flight utterance — wait for
  // the finalized chunk before deciding whether a pause occurred.
  if (previousLine.isPartial) {
    return false;
  }

  // Final update for the same ASR resultId (partial → final) is not a new turn.
  if (incomingLine.resultId === previousLine.resultId) {
    return false;
  }

  return (
    getSegmentGapMs(previousLine, incomingLine) >= options.pauseThresholdMs
  );
};

export const buildCaptionTurns = (
  lines: CaptionLineForTurns[],
  options: BuildCaptionTurnsOptions
): CaptionTurn[] => {
  // Each raw caption line becomes one turn by default; consecutive lines from the
  // same speaker are merged unless speaker changes or a pause split applies.
  return lines.reduce<CaptionTurn[]>((turns, currentLine, index) => {
    const previousLine = index > 0 ? lines[index - 1] : undefined;
    const previousTurn = turns.at(-1);

    // Speaker change always starts a fresh turn, even mid-sentence or across pauses.
    if (currentLine.speakerLabel !== previousTurn?.speakerLabel) {
      turns.push(createTurn(currentLine));
      return turns;
    }

    if (
      previousLine &&
      shouldSplitOnPause(previousLine, currentLine, options)
    ) {
      turns.push(createTurn(currentLine));
      return turns;
    }

    // Same speaker, no split: append text and track the extra resultId for pruning.
    previousTurn.text = `${previousTurn.text} ${currentLine.text}`;
    previousTurn.resultIds.push(currentLine.resultId);
    return turns;
  }, []);
};
